// src/modules/leads/LeadMapView.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaEye, FaFileInvoiceDollar,
  FaSortAlphaDown, FaSortAlphaUp, FaSort, FaSearch, FaTimes, FaPlus,
  FaTrashAlt, FaUndo, FaMapPin
} from 'react-icons/fa';
import formatPhone from '../../components/shared/formatPhone';
import OriginBadge from '../../components/shared/OriginBadge';
import { getPreferredContactConfig } from '../../components/shared/PreferredContactBadge';
import CallModal from '../../components/shared/CallModal';
import SmsModal from './SmsModal';
import EmailModal from './EmailModal';
import AddToRouteButton from '../routes/components/AddToRouteButton';
import { useAuth } from '../auth/AuthContext';
import { fetchAcceptedServiceMap, leadMatchesServiceFilter, getRequestedServiceNames } from './leadEstimateServiceFilter';
import MultiSelectDropdown from '../../components/shared/MultiSelectDropdown';
import { getUnitInfo } from '../estimates/ServiceModal';
import { getLeadEstimates } from './leadService';
import styled from 'styled-components';
import { discountInfo } from '../../utils/discount';

// ─── Styled Components ─────────────────────────────────────────────────────────

const MapViewContainer = styled.div`
  display: flex;
  height: calc(100vh - 160px);
  gap: 0;
  border-radius: ${props => props.theme.borderRadius};
  overflow: hidden;
  background: ${props => props.theme.colors.surface};

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
  }
`;

const Sidebar = styled.div`
  width: 380px;
  min-width: 380px;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.colors.surface};
  border-right: 1px solid ${props => props.theme.colors.inputGray}33;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    min-width: 100%;
    max-height: 50vh;
  }
`;

const SidebarHeader = styled.div`
  padding: ${props => props.theme.spacing(2)};
  border-bottom: 1px solid ${props => props.theme.colors.inputGray}33;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  padding: 0 ${props => props.theme.spacing(1)};
  margin-bottom: ${props => props.theme.spacing(1.5)};

  input {
    flex-grow: 1;
    padding: ${props => props.theme.spacing(1)};
    background: transparent;
    border: none;
    color: ${props => props.theme.colors.textPrimary};
    font-size: 0.85rem;
    &:focus { outline: none; }
  }

  svg {
    color: ${props => props.theme.colors.textSecondary};
    font-size: 0.8rem;
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(1)};
  align-items: center;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 6px 8px;
  border-radius: 4px;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  border: 1px solid ${props => props.theme.colors.inputGray};
  font-size: 0.8rem;
  flex: 1;
`;

const SortButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 4px;
  background: ${props => props.isActive ? props.theme.colors.primary + '22' : props.theme.colors.background};
  color: ${props => props.isActive ? props.theme.colors.primary : props.theme.colors.textSecondary};
  border: 1px solid ${props => props.isActive ? props.theme.colors.primary + '44' : props.theme.colors.inputGray};
  cursor: pointer;
  font-size: 0.75rem;
  white-space: nowrap;

  &:hover {
    background: ${props => props.theme.colors.primary}22;
  }
`;

const MapActionBar = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(1)};
  margin-top: ${props => props.theme.spacing(1.5)};
`;

const MapActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 4px;
  background: ${props => props.variant === 'danger' ? '#e74c3c22' : props.variant === 'primary' ? props.theme.colors.primary + '22' : props.theme.colors.background};
  color: ${props => props.variant === 'danger' ? '#e74c3c' : props.variant === 'primary' ? props.theme.colors.primary : props.theme.colors.textSecondary};
  border: 1px solid ${props => props.variant === 'danger' ? '#e74c3c44' : props.variant === 'primary' ? props.theme.colors.primary + '44' : props.theme.colors.inputGray};
  cursor: pointer;
  font-size: 0.75rem;
  white-space: nowrap;
  flex: 1;
  justify-content: center;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const LeadListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${props => props.theme.spacing(1)};

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.inputGray}44;
    border-radius: 3px;
  }
`;

const LeadCard = styled.div`
  padding: ${props => props.theme.spacing(1.5)};
  margin-bottom: ${props => props.theme.spacing(1)};
  background: ${props => props.isHighlighted ? props.theme.colors.primary + '11' : props.theme.colors.background};
  border: 1px solid ${props => props.isHighlighted ? props.theme.colors.primary + '44' : 'transparent'};
  border-radius: ${props => props.theme.borderRadius};
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 3px solid ${props => props.statusColor || '#95a5a6'};

  &:hover {
    background: ${props => props.theme.colors.primary}11;
    transform: translateX(2px);
  }
`;

const LeadCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
`;

const LeadCardName = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textPrimary};
`;

const LeadCardStatus = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  background-color: ${props => props.color}22;
  color: ${props => props.color};
  white-space: nowrap;
`;

const LeadCardInfo = styled.div`
  font-size: 0.8rem;
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;

  svg {
    font-size: 0.7rem;
    flex-shrink: 0;
  }
`;

const LeadCardActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(1)};
  margin-top: ${props => props.theme.spacing(1)};
`;

const SmallButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  background: ${props => props.variant === 'primary' ? props.theme.colors.primary + '22' : props.theme.colors.surface};
  color: ${props => props.variant === 'primary' ? props.theme.colors.primary : props.theme.colors.textSecondary};
  border: 1px solid ${props => props.variant === 'primary' ? props.theme.colors.primary + '44' : props.theme.colors.inputGray + '44'};
  cursor: pointer;
  font-size: 0.7rem;

  &:hover {
    opacity: 0.8;
  }
`;

const MapContainer = styled.div`
  flex: 1;
  position: relative;
  min-height: 400px;
`;

// ─── Info window (Google Maps InfoWindow takes an HTML string, not React) ──────
//
// Because the popup lives outside React's tree, its buttons carry
// data-map-action / data-lead-id attributes; LeadMapView attaches one delegated
// click handler on the InfoWindow's `domready` and turns those into React state.

const escapeHtml = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// Service names + timeframe + whatever free text we have, so a pin answers
// "what does this person actually want?" without opening the lead.
const buildServiceSection = (lead) => {
  const services = (lead.serviceRequests || [])
    .map(sr => sr?.Service?.service_name)
    .filter(Boolean);
  const uniqueServices = [...new Set(services)];

  const jobInfo = (lead.serviceRequests || [])
    .map(sr => sr?.job_information)
    .find(Boolean);
  // job_observation carries ad-platform metadata on FB leads; keep it short.
  const summary = jobInfo || lead.job_observation || '';
  const trimmedSummary = summary.length > 160 ? `${summary.slice(0, 160)}…` : summary;

  if (!uniqueServices.length && !trimmedSummary && !lead.when_ready) return '';

  const chips = uniqueServices.map(s =>
    `<span style="display:inline-block;padding:2px 8px;margin:0 4px 4px 0;border-radius:10px;font-size:10px;background:#3d5afe22;color:#3d5afe;">${escapeHtml(s)}</span>`
  ).join('');

  return `
    <div style="margin-top:8px;padding-top:8px;border-top:1px solid #e6e6e6;">
      <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.04em;color:#888;margin-bottom:4px;">Service requested</div>
      ${chips || ''}
      ${lead.when_ready ? `<p style="margin:2px 0;font-size:11px;color:#666;"><strong>Timeframe:</strong> ${escapeHtml(lead.when_ready)}</p>` : ''}
      ${trimmedSummary ? `<p style="margin:4px 0 0;font-size:11px;color:#555;line-height:1.45;white-space:pre-wrap;">${escapeHtml(trimmedSummary)}</p>` : ''}
    </div>`;
};

const buildPreferredContactChip = (lead) => {
  const cfg = getPreferredContactConfig(lead.preferred_contact);
  if (!cfg) return ''; // older leads have no preference recorded — show nothing
  return `<span style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:10px;background:${cfg.color}22;color:${cfg.color};border:1px solid ${cfg.color}44;">Prefers ${escapeHtml(cfg.label)}</span>`;
};

const actionBtn = (action, leadId, title, svg, extra = '') =>
  `<button type="button" data-map-action="${action}" data-lead-id="${leadId}" title="${escapeHtml(title)}" ${extra}
     style="display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;margin-right:6px;border:1px solid #d5d5d5;background:#fff;border-radius:6px;cursor:pointer;padding:0;">${svg}</button>`;

// Inline SVGs — an InfoWindow string can't render react-icons components.
const ICONS = {
  phone: '<svg width="13" height="13" viewBox="0 0 512 512" fill="#16A085"><path d="M493.4 24.6l-104-24c-11.3-2.6-22.9 3.3-27.5 13.9l-48 112c-4.2 9.8-1.4 21.3 6.9 28l60.6 49.6c-36 76.7-98.9 140.5-177.2 177.2l-49.6-60.6c-6.8-8.3-18.2-11.1-28-6.9l-112 48C3.9 366.5-2 378.1.6 389.4l24 104C27.1 504.2 36.7 512 48 512c256.1 0 464-207.5 464-464 0-11.2-7.7-20.9-18.6-23.4z"/></svg>',
  sms: '<svg width="13" height="13" viewBox="0 0 512 512" fill="#9B59B6"><path d="M256 32C114.6 32 0 125.1 0 240c0 47.6 19.9 91.2 52.9 126.3C38 405.7 7 439.1 6.5 439.5 0 446.4-1.9 456.4 1.8 465.2 5.4 474 14 480 24 480c66.6 0 116.4-30.7 141.7-49.4 28.3 8.8 58.9 13.4 90.3 13.4 141.4 0 256-93.1 256-208S397.4 32 256 32z"/></svg>',
  email: '<svg width="13" height="13" viewBox="0 0 512 512" fill="#F39C12"><path d="M502.3 190.8c3.9-3.1 9.7-.2 9.7 4.7V400c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V195.6c0-5 5.7-7.8 9.7-4.7 22.4 17.4 52.1 39.5 154.1 113.6 21.1 15.4 56.7 47.8 92.2 47.6 35.7.3 72-32.8 92.3-47.6 102-74.1 131.6-96.3 154-113.7zM256 320c23.2.4 56.6-29.2 73.4-41.4 132.7-96.3 142.8-104.7 173.4-128.7 5.8-4.5 9.2-11.5 9.2-18.9v-19c0-26.5-21.5-48-48-48H48C21.5 64 0 85.5 0 112v19c0 7.4 3.4 14.3 9.2 18.9 30.6 23.9 40.7 32.4 173.4 128.7 16.8 12.2 50.2 41.8 73.4 41.4z"/></svg>',
  route: '<svg width="13" height="13" viewBox="0 0 512 512" fill="#3d5afe"><path d="M416 320h-96c-17.6 0-32-14.4-32-32s14.4-32 32-32h96s96-107 96-160-43-96-96-96-96 43-96 96c0 25.5 22.2 63.4 45.3 96H320c-52.9 0-96 43.1-96 96s43.1 96 96 96h96c17.6 0 32 14.4 32 32s-14.4 32-32 32H185.5c-14 25.4-29.9 49.6-45 71.2 5.6.6 11.3.8 17 .8h258c52.9 0 96-43.1 96-96s-43.1-96-95.5-96zM416 64c17.7 0 32 14.3 32 32s-14.3 32-32 32-32-14.3-32-32 14.3-32 32-32zM96 192c-53 0-96 43-96 96s96 224 96 224 96-171 96-224-43-96-96-96zm0 128c-17.7 0-32-14.3-32-32s14.3-32 32-32 32 14.3 32 32-14.3 32-32 32z"/></svg>',
};

// ─── Quote breakdown ──────────────────────────────────────────────────────────
//
// The leads list only carries each estimate's id/status/total, so line items
// are fetched per lead when its pin opens and swapped into [data-quotes-slot].
// Each line is stacked (name + line total, then "qty × rate" underneath)
// rather than tabular, so it stays readable in a phone-width popup.

const money = (v) => {
  const n = Number(v);
  return Number.isFinite(n)
    ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    : '—';
};

const formatQty = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '—';
};

const quoteStatusChip = (status) => {
  const c = getStatusColor(status);
  return `<span style="padding:1px 7px;border-radius:10px;font-size:10px;background:${c}22;color:${c};white-space:nowrap;">${escapeHtml(status || 'Draft')}</span>`;
};

const buildQuoteCard = (est, itemsHtml) => {
  const discount = discountInfo(est, est.originalSubtotal ?? est.subtotal);
  const hasDiscount = discount.hasDiscount && est.subtotal != null;
  const date = est.createdAt ? new Date(est.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

  return `
    <div style="margin-top:8px;border:1px solid #e3e6ee;border-radius:8px;overflow:hidden;background:#fafbfd;">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:6px;padding:7px 9px;background:#f1f3f9;border-bottom:1px solid #e3e6ee;">
        <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;min-width:0;">
          <a href="/estimates/view/${est.id}" style="font-size:12px;font-weight:700;color:#3d5afe;text-decoration:none;">Quote #${est.id}</a>
          ${quoteStatusChip(est.status)}
        </div>
        ${date ? `<span style="font-size:10px;color:#888;white-space:nowrap;">${escapeHtml(date)}</span>` : ''}
      </div>
      <div style="padding:2px 9px;">${itemsHtml}</div>
      ${hasDiscount ? `
        <div style="display:flex;justify-content:space-between;padding:4px 9px 0;font-size:11px;color:#666;">
          <span>Subtotal</span><span>${money(est.subtotal)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding:2px 9px 0;font-size:11px;color:#c0392b;">
          <span>${escapeHtml(discount.label)}</span><span>−${money(discount.amount)}</span>
        </div>` : ''}
      <div style="display:flex;justify-content:space-between;align-items:baseline;margin-top:4px;padding:7px 9px;border-top:1px solid #e3e6ee;background:#fff;">
        <span style="font-size:11px;text-transform:uppercase;letter-spacing:0.04em;color:#666;">Total</span>
        <span style="font-size:15px;font-weight:700;color:#1b8a4a;">${money(est.totalPrice)}</span>
      </div>
    </div>`;
};

const buildItemRows = (items) => {
  if (!items?.length) {
    return '<p style="margin:6px 0;font-size:11px;color:#999;font-style:italic;">No line items</p>';
  }
  return items.map((it, i) => {
    const unit = getUnitInfo(it.measurementUnit).short;
    return `
      <div style="padding:6px 0;${i ? 'border-top:1px dashed #e3e6ee;' : ''}">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;">
          <span style="font-size:12px;font-weight:600;color:#222;line-height:1.3;">${escapeHtml(it.Service?.service_name || 'Service')}</span>
          <span style="font-size:12px;font-weight:600;color:#222;white-space:nowrap;">${money(it.totalPrice)}</span>
        </div>
        <div style="font-size:11px;color:#777;margin-top:2px;">
          ${formatQty(it.totalArea)} ${escapeHtml(unit)} × ${money(it.pricePerSqft)}/${escapeHtml(unit)}
        </div>
      </div>`;
  }).join('');
};

// state: 'loading' | 'error' | array of full estimates (with items)
const buildQuotesSection = (lead, state) => {
  const summaries = lead.estimates || [];
  if (!summaries.length) return '';

  let cards;
  if (Array.isArray(state)) {
    cards = state.length
      ? state.map(est => buildQuoteCard(est, buildItemRows(est.items))).join('')
      : '<p style="margin:6px 0;font-size:11px;color:#999;">No quotes found</p>';
  } else {
    const note = state === 'error'
      ? '<p style="margin:6px 0;font-size:11px;color:#c0392b;">Couldn\'t load line items — reopen the pin to retry.</p>'
      : '<p style="margin:6px 0;font-size:11px;color:#999;">Loading line items…</p>';
    cards = summaries.map(est => buildQuoteCard(est, note)).join('');
  }

  const count = Array.isArray(state) ? state.length : summaries.length;
  return `
    <div style="margin-top:8px;padding-top:8px;border-top:1px solid #e6e6e6;">
      <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.04em;color:#888;">Quotes (${count})</div>
      <div style="max-height:min(320px,45vh);overflow-y:auto;-webkit-overflow-scrolling:touch;padding-right:2px;">${cards}</div>
    </div>`;
};

// Single source of truth for pin popup markup — this was duplicated between the
// marker click handler and the sidebar-card click handler.
const buildInfoContent = (lead, { statusName, statusColor }) => {
  const address = [lead.street, lead.city, lead.state, lead.zipcode].filter(Boolean).join(', ');

  const hasPhone = !!lead.phone;
  const hasEmail = !!(lead.email && String(lead.email).includes('@'));
  const disabledStyle = 'disabled style="opacity:.35;cursor:not-allowed;"';

  return `
    <div style="padding:8px;width:min(310px,78vw);box-sizing:border-box;font-family:sans-serif;">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:6px;">
        <h4 style="margin:0;font-size:14px;color:#222;">${escapeHtml(lead.name)}</h4>
        <span style="padding:2px 8px;border-radius:10px;font-size:10px;background:${statusColor}22;color:${statusColor};white-space:nowrap;">${escapeHtml(statusName)}</span>
      </div>
      ${buildPreferredContactChip(lead) ? `<div style="margin-bottom:6px;">${buildPreferredContactChip(lead)}</div>` : ''}
      <p style="margin:3px 0;font-size:12px;color:#555;"><strong>Phone:</strong> ${formatPhone(lead.phone) || 'N/A'}</p>
      <p style="margin:3px 0;font-size:12px;color:#555;"><strong>Address:</strong> ${escapeHtml(address) || 'N/A'}</p>
      ${lead.email ? `<p style="margin:3px 0;font-size:12px;color:#555;word-break:break-all;"><strong>Email:</strong> ${escapeHtml(lead.email)}</p>` : ''}
      ${buildServiceSection(lead)}
      <div data-quotes-slot>${buildQuotesSection(lead, 'loading')}</div>
      <div style="margin-top:10px;padding-top:8px;border-top:1px solid #e6e6e6;">
        ${actionBtn('call', lead.id, hasPhone ? 'Call this lead' : 'No phone number on file', ICONS.phone, hasPhone ? '' : disabledStyle)}
        ${actionBtn('sms', lead.id, hasPhone ? 'Send SMS' : 'No phone number on file', ICONS.sms, hasPhone ? '' : disabledStyle)}
        ${actionBtn('email', lead.id, hasEmail ? 'Send email' : 'No email address on file', ICONS.email, hasEmail ? '' : disabledStyle)}
        ${actionBtn('route', lead.id, 'Add to route', ICONS.route)}
        <a href="/leads/view/${lead.id}" style="display:inline-block;margin-top:6px;padding:5px 12px;background:#3d5afe;color:#fff;text-decoration:none;border-radius:4px;font-size:12px;vertical-align:top;">View Lead</a>
      </div>
    </div>`;
};

const InfoWindowContent = styled.div`
  padding: 8px;
  min-width: 220px;

  h4 {
    margin: 0 0 6px 0;
    font-size: 14px;
    color: #222;
  }

  p {
    margin: 3px 0;
    font-size: 12px;
    color: #555;
  }

  a {
    display: inline-block;
    margin-top: 6px;
    padding: 4px 10px;
    background: #3d5afe;
    color: #fff;
    text-decoration: none;
    border-radius: 4px;
    font-size: 12px;

    &:hover {
      background: #2840cb;
    }
  }
`;

const LeadCount = styled.div`
  padding: ${props => props.theme.spacing(1)} ${props => props.theme.spacing(2)};
  font-size: 0.8rem;
  color: ${props => props.theme.colors.textSecondary};
  border-bottom: 1px solid ${props => props.theme.colors.inputGray}33;
`;

// ─── Helper Functions ───────────────────────────────────────────────────────────

const getLeadStatus = (lead) => {
  if (lead.status?.status_name) return lead.status;
  const srStatus = lead.serviceRequests?.[0]?.LeadStatus;
  if (srStatus) return srStatus;
  return null;
};

const getStatusColor = (status) => {
  switch (status) {
    case 'New': return '#3d5afe';
    case 'Prospect': return '#f39c12';
    case 'Estimate Sent': return '#3498db';
    case 'Converted': return '#27ae60';
    case 'Won': return '#9b59b6';
    case 'Customer': return '#28a745';
    case 'Lost': return '#e74c3c';
    case 'Imported': return '#9C27B0';
    case 'In Progress': return '#f9a825';
    case 'Completed': return '#4caf50';
    case 'Accepted': return '#27ae60';
    case 'Declined': return '#e74c3c';
    case 'Sent': return '#3498db';
    case 'Viewed': return '#2ecc71';
    case 'Scheduled': return '#27ae60';
    case 'Draft': return '#f39c12';
    case 'No Status': return '#95a5a6';
    default: return '#95a5a6';
  }
};

const getColorForLead = (lead) => {
  const status = getLeadStatus(lead);
  return status?.color || getStatusColor(status?.status_name || 'No Status');
};

// Create a colored SVG pin marker
const createColoredPin = (color) => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
      <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.268 21.732 0 14 0z" fill="${color}" stroke="#fff" stroke-width="1.5"/>
      <circle cx="14" cy="14" r="6" fill="#fff"/>
    </svg>
  `;
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
};

// ─── Component ──────────────────────────────────────────────────────────────────

const LeadMapView = ({ leads }) => {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  // leadId -> Promise<estimates with items>; dropped on failure so a reopen retries.
  const quotesCacheRef = useRef(new Map());
  const openInfoLeadIdRef = useRef(null);

  // Map filters live in the URL (m-prefixed keys) so they survive a refresh and
  // back/forward navigation. LeadList owns the un-prefixed keys; both merge into
  // the same query string rather than replacing it.
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState(searchParams.get('mq') || '');
  const [sortConfig, setSortConfig] = useState({
    field: searchParams.get('mSortField') || 'city',
    direction: searchParams.get('mSortDir') || 'asc',
  });
  const [dateRange, setDateRange] = useState(searchParams.get('mDate') || 'all');
  // Status, city and service are all ANY-match multi-selects — [] means "all",
  // and a lead matches if it has any one of the selected values. Each uses
  // repeated params (?mStatus=…&mStatus=…) rather than a delimited string,
  // since status/city/service names are free text and could contain a comma.
  // A legacy single-value ?mStatus=Prospect link still reads back correctly;
  // the old ?mStatus=all sentinel is dropped.
  const [statusFilter, setStatusFilter] = useState(
    () => searchParams.getAll('mStatus').filter(s => s && s !== 'all')
  );
  const [cityFilter, setCityFilter] = useState(
    () => searchParams.getAll('mCity').filter(Boolean)
  );
  const [serviceFilter, setServiceFilter] = useState(
    () => searchParams.getAll('mService').filter(Boolean)
  ); // [] = all; ANY-match on requested or accepted-quote services
  const [serviceMap, setServiceMap] = useState(new Map());
  const [acceptedServiceNames, setAcceptedServiceNames] = useState([]);
  const [highlightedLeadId, setHighlightedLeadId] = useState(null);

  // Pin-popup actions. The InfoWindow is plain HTML outside React's tree, so its
  // buttons are bridged into these by a delegated click handler (see below).
  const { user } = useAuth();
  const [actionLead, setActionLead] = useState(null);   // lead the popup acted on
  const [showCallModal, setShowCallModal] = useState(false);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  // Bumped to open AddToRouteButton's picker without a visible trigger.
  const [routeSignal, setRouteSignal] = useState(0);
  // The Maps script loads asynchronously, so infoWindowRef isn't populated on
  // first render — this re-runs the popup-button binding once it exists.
  const [mapReady, setMapReady] = useState(false);
  // "Selective pins" mode — which subset of pins is shown on the map.
  const [isSelectiveMode, setIsSelectiveMode] = useState(searchParams.get('mPins') === '1');
  const [selectedLeadIds, setSelectedLeadIds] = useState(() => {
    const raw = searchParams.get('mPinIds');
    if (!raw) return new Set();
    return new Set(raw.split(',').map(Number).filter(Number.isFinite));
  });

  // Load accepted-quote services once so leads can be filtered by service type
  useEffect(() => {
    let active = true;
    fetchAcceptedServiceMap().then(({ serviceMap: map, availableServices: services }) => {
      if (!active) return;
      setServiceMap(map);
      setAcceptedServiceNames(services);
    });
    return () => { active = false; };
  }, []);

  // Filter options: services on accepted quotes plus services requested on any lead
  const availableServices = useMemo(() => {
    const names = new Set(acceptedServiceNames);
    leads.forEach(lead => getRequestedServiceNames(lead).forEach(n => names.add(n)));
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [acceptedServiceNames, leads]);

  // Extract available statuses from leads
  const availableStatuses = useMemo(() => {
    const statuses = new Set(leads.map(lead => {
      const s = getLeadStatus(lead);
      return s?.status_name || 'No Status';
    }));
    return Array.from(statuses).sort();
  }, [leads]);

  // Extract available cities from leads
  const availableCities = useMemo(() => {
    const cities = new Set(leads.map(lead => lead.city).filter(Boolean));
    return Array.from(cities).sort((a, b) => a.localeCompare(b));
  }, [leads]);

  // Filter leads by date range
  const dateFilteredLeads = useMemo(() => {
    if (dateRange === 'all') return leads;

    let cutoff;
    if (dateRange === 'year') {
      cutoff = new Date(new Date().getFullYear(), 0, 1);
    } else {
      const days = parseInt(dateRange);
      cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
    }

    return leads.filter(lead => new Date(lead.createdAt) >= cutoff);
  }, [leads, dateRange]);

  // Filter and sort leads for the sidebar list
  const filteredLeads = useMemo(() => {
    let result = dateFilteredLeads;

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(lead =>
        lead.name?.toLowerCase().includes(q) ||
        lead.city?.toLowerCase().includes(q) ||
        lead.phone?.includes(q) ||
        lead.email?.toLowerCase().includes(q)
      );
    }

    // Status filter (ANY of the selected statuses)
    if (statusFilter.length > 0) {
      result = result.filter(lead => {
        const s = getLeadStatus(lead);
        const name = s?.status_name || 'No Status';
        return statusFilter.includes(name);
      });
    }

    // City filter (ANY of the selected cities)
    if (cityFilter.length > 0) {
      result = result.filter(lead => cityFilter.includes(lead.city));
    }

    // Service filter (services on the lead's Accepted quote — ANY match)
    if (serviceFilter.length > 0) {
      result = result.filter(lead => leadMatchesServiceFilter(lead, serviceFilter, serviceMap));
    }

    // Sort
    return [...result].sort((a, b) => {
      if (sortConfig.field === 'createdAt') {
        return sortConfig.direction === 'desc'
          ? new Date(b.createdAt) - new Date(a.createdAt)
          : new Date(a.createdAt) - new Date(b.createdAt);
      }
      const aVal = (a[sortConfig.field] || '').toString();
      const bVal = (b[sortConfig.field] || '').toString();
      return sortConfig.direction === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    });
  }, [dateFilteredLeads, searchQuery, statusFilter, cityFilter, serviceFilter,
      serviceMap, sortConfig]);

  // Determine which leads to show on map
  const mapLeads = useMemo(() => {
    if (isSelectiveMode) {
      return filteredLeads.filter(l => selectedLeadIds.has(l.id));
    }
    return filteredLeads;
  }, [filteredLeads, isSelectiveMode, selectedLeadIds]);

  // Leads with valid coordinates
  const mappableLeads = useMemo(() => {
    return mapLeads.filter(l => l.latitude && l.longitude);
  }, [mapLeads]);

  // Persist map filters to the URL so a refresh or back/forward restores them.
  //
  // Touches only the m-prefixed keys, leaving LeadList's (q/status/city/view/…)
  // untouched — the two components share one query string.
  //
  // Selected pin ids are capped: a large selection would blow past practical URL
  // length limits, so past the cap we keep the mode but not the id list rather
  // than writing a URL that browsers may truncate.
  const MAX_PERSISTED_PINS = 60;
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const apply = (key, value, omitWhen) =>
      (value === undefined || value === null || value === '' || value === omitWhen)
        ? params.delete(key)
        : params.set(key, String(value));

    apply('mq', searchQuery);
    apply('mSortField', sortConfig.field, 'city');
    apply('mSortDir', sortConfig.direction, 'asc');
    apply('mDate', dateRange, 'all');

    params.delete('mStatus');
    statusFilter.forEach(s => params.append('mStatus', s));

    params.delete('mCity');
    cityFilter.forEach(c => params.append('mCity', c));

    params.delete('mService');
    serviceFilter.forEach(s => params.append('mService', s));

    if (isSelectiveMode) {
      params.set('mPins', '1');
      const ids = [...selectedLeadIds];
      if (ids.length > 0 && ids.length <= MAX_PERSISTED_PINS) {
        params.set('mPinIds', ids.join(','));
      } else {
        params.delete('mPinIds');
      }
    } else {
      params.delete('mPins');
      params.delete('mPinIds');
    }

    // Only navigate when something actually changed. Writing an identical query
    // string would create a new location, which can re-trigger this effect.
    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [searchQuery, sortConfig, dateRange, statusFilter, cityFilter, serviceFilter,
      isSelectiveMode, selectedLeadIds, searchParams, setSearchParams]);

  // Bridge InfoWindow HTML buttons -> React state.
  //
  // Google rebuilds the popup DOM every time it opens, so the listener is
  // re-attached on each `domready` rather than once at init. One delegated
  // handler on the popup root covers all four actions.
  useEffect(() => {
    if (!infoWindowRef.current || !window.google) return undefined;

    const onDomReady = () => {
      // The popup's outer node isn't exposed by the API; scope the query to the
      // map container so we never bind buttons belonging to another widget.
      const root = mapRef.current;
      if (!root) return;

      root.querySelectorAll('[data-map-action]').forEach((btn) => {
        if (btn.dataset.bound === '1') return; // guard against double-binding
        btn.dataset.bound = '1';

        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const action = btn.getAttribute('data-map-action');
          const leadId = Number(btn.getAttribute('data-lead-id'));
          const lead = (leads || []).find(l => l.id === leadId);
          if (!lead) return;

          setActionLead(lead);
          if (action === 'call') setShowCallModal(true);
          else if (action === 'sms') setShowSmsModal(true);
          else if (action === 'email') setShowEmailModal(true);
          else if (action === 'route') setRouteSignal(s => s + 1);
        });
      });
    };

    const listener = infoWindowRef.current.addListener('domready', onDomReady);
    return () => {
      if (listener && window.google?.maps?.event) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [leads, mapReady]);

  // Initialize Google Map
  useEffect(() => {
    const initMap = () => {
      if (!mapRef.current || !window.google) return;

      const defaultCenter = { lat: 40.7128, lng: -74.0060 }; // NYC default
      googleMapRef.current = new window.google.maps.Map(mapRef.current, {
        zoom: 9,
        center: defaultCenter,
        styles: [
          { elementType: 'geometry', stylers: [{ color: '#212121' }] },
          { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
          { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
          { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
          { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212a37' }] },
          { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca5b3' }] },
          { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
          { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#515c6d' }] },
          { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
        ],
      });
      infoWindowRef.current = new window.google.maps.InfoWindow();
      setMapReady(true);
    };

    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
      script.async = true;
      script.onload = initMap;
      document.body.appendChild(script);
    } else {
      initMap();
    }
  }, []);

  // Open a lead's popup, then fill in its quote line items once they load.
  //
  // Content is passed as a DOM node (not a string) so the quotes slot can be
  // swapped in place without rebuilding the action buttons.
  const openLeadInfo = useCallback((lead, marker) => {
    const statusObj = getLeadStatus(lead);
    const node = document.createElement('div');
    node.innerHTML = buildInfoContent(lead, {
      statusName: statusObj?.status_name || 'No Status',
      statusColor: getColorForLead(lead),
    });

    openInfoLeadIdRef.current = lead.id;
    infoWindowRef.current.setContent(node);
    infoWindowRef.current.open(googleMapRef.current, marker);

    if (!lead.estimates?.length) return;

    const cache = quotesCacheRef.current;
    if (!cache.has(lead.id)) {
      cache.set(lead.id, getLeadEstimates(lead.id).then(res => res.data || []));
    }

    const fill = (state) => {
      // Ignore a late response for a popup that's since been replaced.
      if (openInfoLeadIdRef.current !== lead.id) return;
      const slot = node.querySelector('[data-quotes-slot]');
      if (!slot) return;
      slot.innerHTML = buildQuotesSection(lead, state);
      // Re-set the same node so the InfoWindow re-measures its taller content.
      infoWindowRef.current.setContent(node);
    };

    cache.get(lead.id).then(fill).catch(() => {
      cache.delete(lead.id);
      fill('error');
    });
  }, []);

  // Update markers when mapLeads changes
  const updateMarkers = useCallback(() => {
    if (!googleMapRef.current || !window.google) return;

    // Clear existing markers
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    if (mappableLeads.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();

    mappableLeads.forEach(lead => {
      const color = getColorForLead(lead);
      const position = { lat: parseFloat(lead.latitude), lng: parseFloat(lead.longitude) };

      const marker = new window.google.maps.Marker({
        position,
        map: googleMapRef.current,
        title: lead.name,
        icon: {
          url: createColoredPin(color),
          scaledSize: new window.google.maps.Size(28, 40),
          anchor: new window.google.maps.Point(14, 40),
        },
      });

      marker.addListener('click', () => {
        openLeadInfo(lead, marker);
        setHighlightedLeadId(lead.id);
      });

      bounds.extend(position);
      markersRef.current.push(marker);
    });

    // Fit map to show all markers
    if (mappableLeads.length === 1) {
      googleMapRef.current.setCenter(bounds.getCenter());
      googleMapRef.current.setZoom(14);
    } else {
      googleMapRef.current.fitBounds(bounds, { padding: 50 });
    }
  }, [mappableLeads, openLeadInfo]);

  useEffect(() => {
    updateMarkers();
  }, [updateMarkers]);

  // Handle sort toggle
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Click on a sidebar lead card -> highlight on map
  const handleLeadCardClick = (lead) => {
    setHighlightedLeadId(lead.id);

    if (!lead.latitude || !lead.longitude || !googleMapRef.current) return;

    const position = { lat: parseFloat(lead.latitude), lng: parseFloat(lead.longitude) };
    googleMapRef.current.panTo(position);
    googleMapRef.current.setZoom(15);

    // Find and trigger the marker
    const marker = markersRef.current.find(m => m.getTitle() === lead.name);
    if (marker) openLeadInfo(lead, marker);
  };

  // Toggle selective mode
  const handleClearPins = () => {
    setIsSelectiveMode(true);
    setSelectedLeadIds(new Set());
  };

  const handleResetPins = () => {
    setIsSelectiveMode(false);
    setSelectedLeadIds(new Set());
  };

  const handleAddToMap = (leadId) => {
    setSelectedLeadIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(leadId)) {
        newSet.delete(leadId);
      } else {
        newSet.add(leadId);
      }
      return newSet;
    });
  };

  return (
    <MapViewContainer>
      {/* Left Sidebar - Lead List */}
      <Sidebar>
        <SidebarHeader>
          <SearchBox>
            <FaSearch />
            <input
              type="text"
              placeholder="Search leads..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <FaTimes style={{ cursor: 'pointer' }} onClick={() => setSearchQuery('')} />
            )}
          </SearchBox>

          <FilterRow>
            <FilterSelect value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
              <option value="all">All Leads</option>
              <option value="7">Last 7 Days</option>
              <option value="14">Last 14 Days</option>
              <option value="21">Last 21 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="60">Last 60 Days</option>
              <option value="90">Last 90 Days</option>
              <option value="year">This Year</option>
            </FilterSelect>

            <MultiSelectDropdown
              options={availableStatuses}
              selected={statusFilter}
              onChange={setStatusFilter}
              placeholder="All Statuses"
            />
          </FilterRow>

          <FilterRow style={{ marginTop: '8px' }}>
            <MultiSelectDropdown
              options={availableCities}
              selected={cityFilter}
              onChange={setCityFilter}
              placeholder="All Cities"
            />

            {availableServices.length > 0 && (
              <MultiSelectDropdown
                options={availableServices}
                selected={serviceFilter}
                onChange={setServiceFilter}
                placeholder="All Services"
              />
            )}
          </FilterRow>

          <FilterRow style={{ marginTop: '8px' }}>
            <SortButton
              isActive={sortConfig.field === 'city'}
              onClick={() => handleSort('city')}
            >
              {sortConfig.field === 'city'
                ? (sortConfig.direction === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />)
                : <FaSort />
              }
              City
            </SortButton>

            <SortButton
              isActive={sortConfig.field === 'createdAt'}
              onClick={() => handleSort('createdAt')}
            >
              {sortConfig.field === 'createdAt'
                ? (sortConfig.direction === 'asc' ? <FaSortAlphaDown /> : <FaSortAlphaUp />)
                : <FaSort />
              }
              Date
            </SortButton>
          </FilterRow>

          <MapActionBar>
            <MapActionButton
              variant="danger"
              onClick={handleClearPins}
              disabled={isSelectiveMode && selectedLeadIds.size === 0}
            >
              <FaTrashAlt /> Clear Pins
            </MapActionButton>
            <MapActionButton
              variant="primary"
              onClick={handleResetPins}
              disabled={!isSelectiveMode}
            >
              <FaUndo /> Reset All
            </MapActionButton>
          </MapActionBar>
        </SidebarHeader>

        <LeadCount>
          {filteredLeads.length} lead{filteredLeads.length !== 1 ? 's' : ''}
          {isSelectiveMode && ` | ${selectedLeadIds.size} on map`}
          {' | '}{mappableLeads.length} mappable
        </LeadCount>

        <LeadListContainer>
          {filteredLeads.map(lead => {
            const statusObj = getLeadStatus(lead);
            const statusName = statusObj?.status_name || 'No Status';
            const statusColor = getColorForLead(lead);
            const isOnMap = !isSelectiveMode || selectedLeadIds.has(lead.id);

            return (
              <LeadCard
                key={lead.id}
                statusColor={statusColor}
                isHighlighted={highlightedLeadId === lead.id}
                onClick={() => handleLeadCardClick(lead)}
              >
                <LeadCardHeader>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minWidth: 0 }}>
                    <LeadCardName>{lead.name}</LeadCardName>
                    {lead.origin?.origin_name && (
                      <OriginBadge origin={lead.origin} size="sm" />
                    )}
                  </div>
                  <LeadCardStatus color={statusColor}>{statusName}</LeadCardStatus>
                </LeadCardHeader>

                <LeadCardInfo>
                  <FaMapMarkerAlt />
                  {lead.city || 'No city'}{lead.state ? `, ${lead.state}` : ''}
                </LeadCardInfo>

                <LeadCardInfo>
                  <FaPhoneAlt />
                  {formatPhone(lead.phone) || 'No phone'}
                </LeadCardInfo>

                {lead.estimates?.length > 0 && (
                  <LeadCardInfo>
                    <FaFileInvoiceDollar />
                    {lead.estimates.length} estimate{lead.estimates.length !== 1 ? 's' : ''}
                  </LeadCardInfo>
                )}

                <LeadCardActions>
                  <SmallButton
                    variant="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/leads/view/${lead.id}`);
                    }}
                  >
                    <FaEye /> View
                  </SmallButton>

                  {isSelectiveMode && (
                    <SmallButton
                      variant={isOnMap ? '' : 'primary'}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddToMap(lead.id);
                      }}
                    >
                      <FaMapPin /> {isOnMap ? 'Remove' : 'Add to Map'}
                    </SmallButton>
                  )}
                </LeadCardActions>
              </LeadCard>
            );
          })}

          {filteredLeads.length === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#aaa' }}>
              No leads found
            </div>
          )}
        </LeadListContainer>
      </Sidebar>

      {/* Right Side - Google Map */}
      <MapContainer ref={mapRef} />

      {/* Actions launched from a pin popup. The popup lives outside React, so
          the modals are rendered here and driven by the bridged state above. */}
      {actionLead && (
        <>
          <CallModal
            isOpen={showCallModal}
            onClose={() => setShowCallModal(false)}
            customerPhone={actionLead.phone}
            customerName={actionLead.name}
            leadId={actionLead.id}
          />

          {showSmsModal && (
            <SmsModal
              isOpen={showSmsModal}
              onClose={() => setShowSmsModal(false)}
              lead={{
                id: actionLead.id,
                name: actionLead.name,
                phone: actionLead.phone,
                city: actionLead.city,
                book_a_call_url: actionLead.book_a_call_url,
                estimates: actionLead.estimates,
              }}
              userId={user?.id}
            />
          )}

          {showEmailModal && (
            <EmailModal
              isOpen={showEmailModal}
              onClose={() => setShowEmailModal(false)}
              lead={{
                id: actionLead.id,
                name: actionLead.name,
                email: actionLead.email,
              }}
              userId={user?.id}
            />
          )}

          {/* Trigger hidden — opened by the popup's route button via openSignal */}
          <AddToRouteButton leadId={actionLead.id} hideTrigger openSignal={routeSignal} />
        </>
      )}
    </MapViewContainer>
  );
};

export default LeadMapView;
